import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/utils/i18n';
import IkiSymbol from '@/components/IkiSymbol';
import '@/styles/HealingJourneys.css';

// HYPSEYA Emotional Soundscapes - clean, therapeutic, modern ambient tracks
// Structure prepared for 6-8 soundscapes (awaiting final list from ElevenLabs)
const emotionalSoundscapes = [
  { 
    id: "soundscape_cellular_renewal", 
    title: "Cellular Renewal", 
    duration: "5:00", 
    theme: "Regeneration & Deep Rest", 
    description: "A grounding soundscape for restoration and renewal",
    file_url: "/assets/audio/ikiminds_cellular_renewal.wav" 
  }
  // Additional soundscapes will be added here after testing:
  // - Grounding
  // - Clarity
  // - Integration
  // - Focus
  // - Emotional Expansion
];

function HealingJourneys() {
  const { t } = useTranslation();
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const initializeAudioContext = () => {
    // Initialize audio context for visualization on first play
    if (audioRef.current && !analyserRef.current) {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaElementSource(audioRef.current);
        source.connect(analyser);
        analyser.connect(audioContext.destination);
        analyser.fftSize = 256;
        analyserRef.current = analyser;
        audioContextRef.current = audioContext;
      } catch (error) {
        console.error('Error initializing audio context:', error);
      }
    }
  };

  const handlePlayTrack = async (track) => {
    if (currentTrack?.id === track.id && isPlaying) {
      // Pause current track
      audioRef.current?.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animationRef.current);
    } else {
      // Initialize audio context on first play (after user interaction)
      if (!analyserRef.current) {
        initializeAudioContext();
      }
      
      // Stop current track if playing different one
      if (audioRef.current && currentTrack?.id !== track.id) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        cancelAnimationFrame(animationRef.current);
      }
      
      // Play new track
      setLoading(true);
      setCurrentTrack(track);
      
      if (audioRef.current) {
        audioRef.current.src = track.file_url;
        audioRef.current.load(); // Ensure the audio is loaded
        audioRef.current.volume = 0.8; // Set reasonable volume
        
        try {
          // Resume audio context if suspended (browser autoplay policy)
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          
          // Mobile Safari requires user interaction, so this should work after click
          const playPromise = audioRef.current.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
                setLoading(false);
                startVisualization(track.id);
              })
              .catch((error) => {
                console.error('Error playing audio:', error);
                setLoading(false);
                // Retry once for mobile
                setTimeout(() => {
                  audioRef.current?.play()
                    .then(() => {
                      setIsPlaying(true);
                      startVisualization(track.id);
                    })
                    .catch(err => console.error('Retry failed:', err));
                }, 100);
              });
          }
        } catch (error) {
          console.error('Error playing audio:', error);
          setLoading(false);
        }
      }
    }
  };

  const startVisualization = (trackId) => {
    const card = document.getElementById(`track-${trackId}`);
    if (!card || !analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const animate = () => {
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const scale = 1 + (average / 255) * 0.15; // Subtle pulsing based on amplitude
      
      if (card) {
        card.style.transform = `scale(${scale})`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTrack(null);
    cancelAnimationFrame(animationRef.current);
  };

  return (
    <div className="healing-journeys-container">
      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 1000,
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          color: '#1E6B68',
          border: '2px solid #B8D3CF',
          borderRadius: '24px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(30, 107, 104, 0.15)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#1E6B68';
          e.currentTarget.style.color = '#fff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.color = '#1E6B68';
        }}
      >
        ← Back
      </button>

      <div className="healing-journeys-header">
        <h1>{t('healingSuite.title')}</h1>
        <p className="header-quote">{t('healingSuite.headerQuote')}</p>
        <button 
          className="about-button"
          onClick={() => setShowAbout(true)}
        >
          <IkiSymbol name="info" size={18} />
          {t('healingSuite.aboutButton')}
        </button>
      </div>

      <div className="healing-tracks-grid">
        {emotionalSoundscapes.map((track) => (
          <div
            key={track.id}
            id={`track-${track.id}`}
            className={`healing-track-card ${currentTrack?.id === track.id ? 'active' : ''}`}
            onClick={() => handlePlayTrack(track)}
          >
            <h3 className="track-title">{track.title}</h3>
            <p className="track-theme">{track.theme}</p>
            {track.description && (
              <p className="track-description" style={{ 
                fontSize: '0.85rem', 
                color: '#666', 
                marginTop: '8px' 
              }}>
                {track.description}
              </p>
            )}
            <div className="track-footer">
              <span className="track-duration">{track.duration}</span>
              <div className="track-play-indicator">
                {loading && currentTrack?.id === track.id ? (
                  <span className="loading-spinner"></span>
                ) : currentTrack?.id === track.id && isPlaying ? (
                  <IkiSymbol name="pause" size={20} />
                ) : (
                  <IkiSymbol name="play" size={20} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hidden audio player */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        onError={() => setLoading(false)}
      />

      {/* About Modal */}
      {showAbout && (
        <div className="healing-about-modal" onClick={() => setShowAbout(false)}>
          <div className="healing-about-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="close-about-button"
              onClick={() => setShowAbout(false)}
            >
              <IkiSymbol name="close" size={24} />
            </button>
            <h2>{t('healingSuite.aboutTitle')}</h2>
            <p className="about-body">{t('healingSuite.aboutBody')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default HealingJourneys;
